import React, { useEffect, useState, useCallback, useRef } from "react";
import { Typography, TextField, alpha } from "@mui/material";
import LimitedTextField from "./LimitedTextField";
import { LIMITATION_TYPES } from "../../../utils/static/limitation";

export const styleForTextField = () => {
  return {
    "& .MuiInputLabel-root": {
      fontWeight: 500,
      letterSpacing: 0.3,
      color: "text.secondary",
      "&.Mui-focused": {
        color: "primary.main",
      },
      "&.Mui-disabled": {
        color: (theme) => theme.palette.text.disabled,
      },
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      transition: "all 0.25s ease-in-out",
      "&:hover:not(.Mui-disabled) fieldset": {
        borderColor: "primary.main",
      },
      "&.Mui-focused fieldset": {
        borderColor: "primary.main",
        boxShadow: (theme) =>
          `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`,
      },
      "&.Mui-disabled": {
        backgroundColor: (theme) =>
          alpha(theme.palette.action.disabledBackground, 0.05),
        cursor: "not-allowed",

        "& fieldset": {
          borderColor: (theme) => alpha(theme.palette.action.disabled, 0.5),
        },

        "& input, & textarea": {
          color: (theme) => theme.palette.text.disabled,
          cursor: "not-allowed",
          "-webkit-text-fill-color": (theme) => theme.palette.text.disabled,
        },

        "& .MuiSelect-select": {
          color: (theme) => theme.palette.text.disabled,
          cursor: "not-allowed",
        },

        "& .MuiInputAdornment-root": {
          color: (theme) => theme.palette.action.disabled,
        },

        "& .MuiSvgIcon-root": {
          color: (theme) => theme.palette.action.disabled,
        },
      },
    },
    "& .MuiSelect-select": {
      fontSize: "0.95rem",
      fontWeight: 500,
      letterSpacing: 0.2,
      color: "text.primary",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      fontSize: "0.75rem",
      "&.Mui-disabled": {
        color: (theme) => theme.palette.text.disabled,
      },
    },
    "& .MuiInputAdornment-root.Mui-disabled": {
      color: (theme) => theme.palette.action.disabled,
    },
  };
};

export const YearsClaim = ({
  value,
  handleInputChange,
  reference,
  numberOfYears,
  disabled = false,
  canEdit = true,
}) => {
  if (!canEdit) {
    return (
      <TextFieldForViewOnly value={value || "-"} sx={{ textAlign: "center" }} />
    );
  }

  return (
    <LimitedTextField
      value={value}
      onChange={handleInputChange}
      limitationType={LIMITATION_TYPES.ONLY_NUMBERS}
      maxValue={numberOfYears}
      warningMessage={
        numberOfYears !== null && numberOfYears !== -1
          ? `Value is limited to ${numberOfYears} years`
          : ""
      }
      reference={reference}
      disabled={disabled}
    />
  );
};

export const TextFieldForName = React.memo(
  ({
    value,
    onChange,
    reference = null,
    charLimit = null,
    disabled = false,
    label = "",
    helperText = "",
    error = false,
    sx = {},
    InputProps = {},
    inputProps = {},
    size = "small",
    debounceMs = 300,
    canEdit = true,
    viewOnlyAlign = "left",
    autoFocus = false,
    placeholder = "Enter your text here...",
  }) => {
    const normalizedValue = value ?? "";
    const [inputValue, setInputValue] = useState(normalizedValue);
    const [isLimitReached, setIsLimitReached] = useState(false);
    const isFocusedRef = useRef(false);
    const latestOnChangeRef = useRef(onChange);
    const latestReferenceRef = useRef(reference);

    React.useEffect(() => {
      latestOnChangeRef.current = onChange;
      latestReferenceRef.current = reference;
    }, [onChange, reference]);

    React.useEffect(() => {
      if (!isFocusedRef.current) {
        setInputValue(normalizedValue);
      }
    }, [normalizedValue]);

    // Debounce logic
    useEffect(() => {
      if (inputValue === normalizedValue || isLimitReached) {
        return undefined;
      }

      const handler = setTimeout(() => {
        latestOnChangeRef.current(inputValue, latestReferenceRef.current);
      }, debounceMs);

      return () => clearTimeout(handler); // cleanup old timer
    }, [inputValue, debounceMs, normalizedValue, isLimitReached]);

    const handleChange = useCallback(
      (e) => {
        const newValue = e.target.value;

        if (charLimit && newValue.length > charLimit) {
          setIsLimitReached(true);
          return;
        }

        setIsLimitReached(false);
        setInputValue(newValue); // only update local state, not parent yet
      },
      [charLimit]
    );

    const handleBlur = useCallback(() => {
      isFocusedRef.current = false;

      if (inputValue !== normalizedValue && !isLimitReached) {
        latestOnChangeRef.current(inputValue, latestReferenceRef.current);
      }
    }, [inputValue, normalizedValue, isLimitReached]);

    if (!canEdit) {
      return (
        <TextFieldForViewOnly
          value={inputValue}
          sx={{ textAlign: viewOnlyAlign, ...sx }}
        />
      );
    }

    return (
      <TextField
        label={label}
        value={inputValue}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={handleBlur}
        onChange={handleChange}
        variant='outlined'
        size={size}
        fullWidth
        autoFocus={autoFocus}
        helperText={
          disabled
            ? helperText
            : isLimitReached
            ? `${inputValue.length}/${charLimit || "∞"} characters` +
              (isLimitReached ? ` (limit reached)` : "")
            : helperText
        }
        error={error || isLimitReached}
        inputProps={{
          ...inputProps,
          maxLength: charLimit || undefined,
          style: { textAlign: "left", ...inputProps.style },
        }}
        placeholder={placeholder}
        sx={sx}
        InputProps={InputProps}
        disabled={disabled}
      />
    );
  }
);

export const TextFieldForViewOnly = ({ value, sx = {} }) => {
  return (
    <Typography
      variant='body2'
      sx={{
        fontWeight: 400,
        ...sx,
      }}
    >
      {value || ""}
    </Typography>
  );
};
